import { createSelector } from 'reselect';
import { getCurrentCompanyEdrpou } from 'selectors/app.selectors';
import { Signature, Signer } from 'services/documents/ts/types';

import { StoreState } from '../../types/store';
import { GroupMembers } from '../../types/user';
import {
    CommonDocumentColumnKey,
    DocumentListDocumentItem,
    DocumentListTableSettingItem,
    DocumentsRequestStatus,
    IDocumentListPinnedIconsSettings,
    TDocumentListPinnedIconsSettingsMap,
} from './types';

import {
    DEFAULT_PINNED_ICON_WIDTH,
    DEFAULT_PINNED_RIGHT_COLUMN_WIDTH,
    MIN_PINNED_COLUMN_WIDTH,
} from './GridTable/constants';

export const getDocumentListSlice = (state: StoreState) => state.documentList;

export const getAllDocuments = (state: StoreState) =>
    getDocumentListSlice(state).documents;

export const getSelectedDocuments = createSelector(
    getAllDocuments,
    (documents) => documents.filter((item) => item.selected),
);

export const getDocumentListFilterBy = (state: StoreState) =>
    getDocumentListSlice(state).filterBy;

export const getDocumentListSortType = (state: StoreState) =>
    getDocumentListSlice(state).sortType;

export const getDocumentListZakupkiEdrpouStatusMap = (state: StoreState) =>
    getDocumentListSlice(state).zakupkiEdrpouStatusMap;

export const getDocumentListDocuments = (
    state: StoreState,
): DocumentListDocumentItem[] => getDocumentListSlice(state).documents;

export const getDocumentsRequestStatus = (
    state: StoreState,
): DocumentsRequestStatus => getDocumentListSlice(state).documentsRequest;

export const getDocumentListDocumentsMap = createSelector(
    getDocumentListDocuments,
    (documents) =>
        documents.reduce((acc, item) => {
            acc[item.id] = item;
            return acc;
        }, {} as Record<string, DocumentListDocumentItem>),
);

export const getDocumentListTableSettings = (
    state: StoreState,
): DocumentListTableSettingItem[] => getDocumentListSlice(state).tableSettings;

export const getDocumentListPinnedIconsSettings = (
    state: StoreState,
): TDocumentListPinnedIconsSettingsMap =>
    getDocumentListSlice(state).pinnedIconsSettings;

export const getTableRightInactiveIconsCount = createSelector(
    getDocumentListPinnedIconsSettings,
    (iconsSettings: TDocumentListPinnedIconsSettingsMap): number =>
        Object.values(iconsSettings).filter(
            (item) => item.side === 'right' && !item.isActive,
        ).length,
);

export const getActualRightPinnedColumnWidth = createSelector(
    getTableRightInactiveIconsCount,
    (countInactiveOptions: number): number => {
        const columnWidth =
            DEFAULT_PINNED_RIGHT_COLUMN_WIDTH -
            countInactiveOptions * DEFAULT_PINNED_ICON_WIDTH;

        if (columnWidth < MIN_PINNED_COLUMN_WIDTH) {
            return MIN_PINNED_COLUMN_WIDTH;
        }

        return columnWidth;
    },
);

export const getPinnedIconsSettingsArray = createSelector(
    getDocumentListPinnedIconsSettings,
    (
        pinnedIconsSettings: TDocumentListPinnedIconsSettingsMap,
    ): IDocumentListPinnedIconsSettings[] => Object.values(pinnedIconsSettings),
);

export const getDocumentListActiveTableSettings = createSelector(
    getDocumentListTableSettings,
    (tableSettings) => tableSettings.filter((item) => item.isActive),
);

export const getDocumentListVisibleColumnsMap = createSelector(
    getDocumentListTableSettings,
    (tableSettings) =>
        tableSettings
            .filter((item) => !item.field)
            .reduce((acc, item) => {
                acc[item.name as CommonDocumentColumnKey] = item.isActive;
                return acc;
            }, {} as Record<CommonDocumentColumnKey, boolean>),
);

export const getActiveCustomCompanyFields = createSelector(
    getDocumentListActiveTableSettings,
    (activeTableSettings) => activeTableSettings.filter((item) => item.field),
);

export const getIsDocumentViewLockedPopupShow = (state: StoreState): boolean =>
    getDocumentListSlice(state).isDocumentViewLockedPopupShow;

interface ModifiedSignerData {
    email?: string;
    roleId?: string;
    order?: Nullable<number>;
    groupId?: string;
    members?: GroupMembers[];
    isIndependentSigning?: boolean;
}

interface ModifiedSignerDataToRender extends ModifiedSignerData {
    hasSigned?: boolean;
}

interface ModifiedDocumentsData {
    docId: string;
    hasOrder: boolean;
    allSignersToRender: ModifiedSignerDataToRender[];
    allSignersWithInpedendSigning: ModifiedSignerDataToRender[];
    allSignersWithoutInpedendSigning: ModifiedSignerDataToRender[];
}

function getModifiedSigners(signers: Signer[]) {
    return signers.map(({ role, roleId, order, groupId, group }) => {
        if (roleId) {
            return {
                email: role.user.email,
                roleId,
                order,
                isIndependentSigning: false,
            };
        } else {
            return {
                name: group.name,
                groupId,
                order,
                members: group.members,
                isIndependentSigning: false,
            };
        }
    });
}

function getModifiedSignatures(signatures: Signature[], edrpou: string) {
    return signatures
        .filter(({ keyOwnerEdrpou, stampOwnerEdrpou }) => {
            return (keyOwnerEdrpou ?? stampOwnerEdrpou) === edrpou;
        })
        .map(({ user, roleId }) => {
            return {
                email: user.email,
                roleId,
                order: null,
                isIndependentSigning: true,
            };
        });
}

function getAllSignersToRender(
    modifiedSigners: ModifiedSignerData[],
    modifiedSignatures: ModifiedSignerData[],
): ModifiedSignerDataToRender[] {
    // IMPORTANT SEQUENCE!!!
    if (modifiedSigners.length) {
        return modifiedSigners.map((signer) => {
            const hasSigned = !!modifiedSignatures.find((signature) => {
                if (signer.roleId) {
                    return signature.roleId === signer.roleId;
                } else {
                    return signer.members?.some(
                        (member) => member.role.id === signature.roleId,
                    );
                }
            });
            return {
                ...signer,
                email: signer.email || 'ТОВ "ВЧАСНО СЕРВІС"',
                hasSigned,
            };
        });
    } else
        return modifiedSignatures.map((signer) => {
            return {
                ...signer,
                email: signer.email || 'ТОВ "ВЧАСНО СЕРВІС"',
                hasSigned: true,
            };
        });
}

const getFilteredAllSigners = (
    signerArray: ModifiedSignerDataToRender[],
    withIndependSigning = false,
) => {
    return signerArray.filter((signer) => {
        if (withIndependSigning) {
            return signer.isIndependentSigning;
        }
        return !signer.isIndependentSigning;
    });
};

function getSignerColumnData(
    documents: DocumentListDocumentItem[],
    edrpou: string,
) {
    return documents.reduce(
        (modifiedDocuments: Record<string, ModifiedDocumentsData>, doc) => {
            const { signatures, signers, id } = doc;
            const modifiedSigners = getModifiedSigners(signers);
            const modifiedSignatures = getModifiedSignatures(
                signatures,
                edrpou,
            );
            const allSignersToRender = getAllSignersToRender(
                modifiedSigners,
                modifiedSignatures,
            );
            const hasOrder = modifiedSigners.some((signer) => signer.order);
            const filteredSignersWithInpedendSigning = getFilteredAllSigners(
                allSignersToRender,
                true,
            );
            const filteredSignersWithoutInpedendSigning = getFilteredAllSigners(
                allSignersToRender,
            );
            const signerBlockData: ModifiedDocumentsData = {
                docId: id,
                hasOrder,
                allSignersToRender,
                allSignersWithInpedendSigning: filteredSignersWithInpedendSigning,
                allSignersWithoutInpedendSigning: filteredSignersWithoutInpedendSigning,
            };
            modifiedDocuments[id] = signerBlockData;
            return modifiedDocuments;
        },
        {} as Record<string, ModifiedDocumentsData>,
    );
}

export const getSignerColumnDataSelector = createSelector(
    getDocumentListDocuments,
    getCurrentCompanyEdrpou,
    (documents, edrpou = '') => getSignerColumnData(documents, edrpou),
);
